/**
 * Reading a day's calendar: where "now" sits among the events, how to say each
 * one in Ukrainian, and how they stack when they overlap.
 *
 * Separate from `lib/format.ts` because these are judgements, not renderings —
 * "these two meetings collide" and "that day is «Завтра»" are claims the UI
 * makes about the clock, and a claim deserves a test. `ExternalContext.calendar_*`
 * and `GET /api/calendar/day` supply the data (CONTRACTS.md §1.11).
 */

import type { CalendarEvent } from "../types";
import { shiftIsoDay } from "./day";
import { weekdayShort } from "./format";

/** Minutes since midnight for `"14:30"`. `-1` for an all-day event. */
export function startMinutes(event: CalendarEvent): number {
  if (event.all_day || !event.start_hm) return -1;
  const [h, m] = event.start_hm.split(":");
  const hours = Number(h);
  const mins = Number(m);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return -1;
  return hours * 60 + mins;
}

/**
 * When the event ends: Google's own `end_hm` when it sent one, otherwise the
 * start plus the duration. Empty string when there is no start.
 *
 * Google is preferred because it knows about the cases arithmetic gets wrong —
 * a DST night, an event whose length was rounded on the way in. The fallback
 * stays because `end_hm` is null for every all-day event and for any payload
 * older than the 2026-09-07 contract.
 */
export function endHm(event: CalendarEvent): string {
  const start = startMinutes(event);
  if (start < 0) return "";
  if (event.end_hm && event.end_hm !== event.start_hm) return event.end_hm;
  // A meeting running past midnight would wrap to the small hours rather than
  // reading "25:30"; the calendar itself splits those, so this only guards
  // against a bad duration.
  const end = (start + Math.max(0, event.minutes)) % (24 * 60);
  const h = Math.floor(end / 60);
  const m = end % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * `"17:00 – 18:00"`, or `"17:00"` when the event has no duration.
 *
 * An all-day event gets a word, not a fake time: a calendar that renders
 * «00:00» for a birthday is stating a meeting time that does not exist.
 */
export function eventTimeLabel(event: CalendarEvent): string {
  if (event.all_day || !event.start_hm) return "весь день";
  const end = endHm(event);
  return event.minutes > 0 && end ? `${event.start_hm} – ${end}` : event.start_hm;
}

/** Minutes since midnight for a Date — the clock the phases are measured against. */
export function nowMinutes(at: Date = new Date()): number {
  return at.getHours() * 60 + at.getMinutes();
}

/**
 * How a day is named next to today: «Сьогодні» / «Завтра» / «Вчора», and
 * «Ср, 10 вересня» for anything further out.
 *
 * The three words beat a date for the days he actually looks at — «Завтра»
 * is read without arithmetic, «8 вересня» is not — and the weekday survives in
 * the long form because "which day of the week is that" is the next question
 * a date raises.
 */
export function dayLabel(day: string, today: string): string {
  if (day === today) return "Сьогодні";
  const delta = daysBetween(today, day);
  if (delta === 1) return "Завтра";
  if (delta === -1) return "Вчора";
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return `${weekdayShort(day)}, ${date.toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
  })}`;
}

/** Whole days from `from` to `to`, both ISO `YYYY-MM-DD`. Negative into the past. */
function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00`).getTime();
  const b = new Date(`${to}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.NaN;
  return Math.round((b - a) / 86_400_000);
}

/** «8 вересня» — the genitive form the UA locale prints for a day of a month. */
function dayMonth(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
  });
}

/**
 * «8 – 14 вересня» / «28 вересня – 4 жовтня» — the week a calendar is showing.
 *
 * The month is printed once when both ends share it, because «8 вересня – 14
 * вересня» says the same word twice in a label that is read at a glance. The
 * year appears only when it is not the year the reader is standing in: on a
 * week of this year it is noise, and on any other week its absence is the
 * mistake. A week that straddles New Year carries a year on each side — that
 * is the one case where a single trailing year would be wrong.
 *
 * @param monday the ISO day the week starts on (see `lib/day.ts::startOfWeek`).
 * @param today the anchor year, the summary's own today.
 */
export function weekLabel(monday: string, today: string): string {
  const first = new Date(`${monday}T00:00:00`);
  if (Number.isNaN(first.getTime())) return monday;
  const sunday = shiftIsoDay(monday, 6);
  const last = new Date(`${sunday}T00:00:00`);

  const thisYear = new Date(`${today}T00:00:00`).getFullYear();
  const fromYear = first.getFullYear();
  const toYear = last.getFullYear();

  if (fromYear !== toYear) {
    return `${dayMonth(monday)} ${fromYear} – ${dayMonth(sunday)} ${toYear}`;
  }
  const sameMonth = first.getMonth() === last.getMonth();
  const span = sameMonth
    ? `${first.getDate()} – ${dayMonth(sunday)}`
    : `${dayMonth(monday)} – ${dayMonth(sunday)}`;
  return fromYear === thisYear ? span : `${span} ${fromYear}`;
}

/**
 * «Сьогодні, 15:00 – 16:00» / «Завтра, 19:00 – 20:00» / «Ср, 10 вересня, 18:00»
 * — one line saying when an event on the horizon happens.
 *
 * An all-day event stops at the day: «Чт, 25 вересня». Appending «весь день»
 * to a holiday would spend a whole line saying that a holiday lasts a day.
 */
export function eventWhenLabel(event: CalendarEvent, today: string): string {
  const day = dayLabel(event.day, today);
  if (event.all_day || !event.start_hm) return day;
  return `${day}, ${eventTimeLabel(event)}`;
}

/* ------------------------------------------------- the vertical hour grid */

/** The hours «Мій день» always draws, before any event stretches them. */
export const GRID_FROM_MIN = 8 * 60;
export const GRID_TO_MIN = 22 * 60;

/** A meeting shorter than this is still given this much of the grid. */
const MIN_EVENT_MIN = 15;

/** One timed event, placed. `column` of `columns` is its share of the width. */
export interface DayBlock {
  event: CalendarEvent;
  /** Minutes since midnight. */
  start: number;
  end: number;
  column: number;
  columns: number;
}

export interface DayLayout {
  /** The window the grid draws, whole hours, minutes since midnight. */
  from: number;
  to: number;
  blocks: DayBlock[];
  /** Events with no hour of their own — the strip above the grid. */
  allDay: CalendarEvent[];
}

/** Minutes since midnight for `"09:30"`; `-1` for anything unreadable. */
function hmMinutes(hm: string): number {
  const [h, m] = hm.split(":");
  const hours = Number(h);
  const mins = Number(m);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return -1;
  return hours * 60 + mins;
}

/** Where the block stops: Google's end, else the duration, never before it starts. */
function blockEnd(event: CalendarEvent, start: number): number {
  const ends = endHm(event);
  let end = ends ? hmMinutes(ends) : start + event.minutes;
  // An event running to midnight sends `end_hm: "00:00"`, which is *before*
  // its start on this clock — the duration is the only thing left to trust.
  if (end <= start) end = start + Math.max(event.minutes, MIN_EVENT_MIN);
  return Math.min(24 * 60, Math.max(end, start + MIN_EVENT_MIN));
}

/**
 * A day as the vertical grid draws it: the hour window, the all-day strip, and
 * every timed event with its side-by-side column.
 *
 * The window is 08:00–22:00 stretched — never shrunk — to cover every event and,
 * on the real today, the current minute: a grid that hides the 07:00 flight or
 * the «зараз» line is worse than a tall one.
 *
 * Overlapping events share the width the way Google Calendar does: events are
 * grouped into clusters that touch, and inside a cluster each event takes the
 * first column free at its start time. `columns` is the cluster's width, so two
 * meetings at 10:00 are halves and a third makes thirds — of that cluster only,
 * not of the whole day.
 *
 * @param nowMin minutes since midnight when the grid is showing today, else null.
 */
export function layoutDay(events: CalendarEvent[], nowMin: number | null): DayLayout {
  const allDay = events.filter((e) => e.all_day || !e.start_hm);

  const timed = events
    .filter((e) => !e.all_day && e.start_hm)
    .map((event) => {
      const start = startMinutes(event);
      return { event, start, end: blockEnd(event, start) };
    })
    .filter((b) => b.start >= 0)
    // Longest first among equal starts, so the wide block takes the left
    // column and the short one nests beside it rather than under it.
    .sort((a, b) => a.start - b.start || b.end - a.end);

  let from = GRID_FROM_MIN;
  let to = GRID_TO_MIN;
  for (const block of timed) {
    from = Math.min(from, block.start);
    to = Math.max(to, block.end);
  }
  if (nowMin !== null) {
    from = Math.min(from, nowMin);
    to = Math.max(to, nowMin + 30);
  }
  from = Math.max(0, Math.floor(from / 60) * 60);
  to = Math.min(24 * 60, Math.ceil(to / 60) * 60);
  if (to <= from) to = Math.min(24 * 60, from + 60);

  const blocks: DayBlock[] = [];
  let cluster: { event: CalendarEvent; start: number; end: number; column: number }[] = [];
  let clusterEnd = -1;
  /** Ends in each column so far — the index of the first free one is the answer. */
  let columnEnds: number[] = [];

  const flush = () => {
    for (const block of cluster) blocks.push({ ...block, columns: columnEnds.length || 1 });
    cluster = [];
    columnEnds = [];
    clusterEnd = -1;
  };

  for (const block of timed) {
    if (cluster.length > 0 && block.start >= clusterEnd) flush();
    let column = columnEnds.findIndex((end) => end <= block.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(block.end);
    } else {
      columnEnds[column] = block.end;
    }
    cluster.push({ ...block, column });
    clusterEnd = Math.max(clusterEnd, block.end);
  }
  flush();

  return { from, to, blocks, allDay };
}
